import { useState, useRef, useEffect } from "react";
import { Reveal, Eyebrow, Btn } from "./primitives.jsx";
import { useSiteContent } from "../content.js";

export default function Apply() {
  const { apply: copy } = useSiteContent();
  const [form, setForm] = useState({
    name: "",
    email: "",
    date: "",
    group: "2",
    about: "",
    how: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const sectionRef = useRef(null);
  const fieldRefs = {
    name: useRef(null),
    email: useRef(null),
    date: useRef(null),
    about: useRef(null),
  };

  const upcoming = Array.isArray(copy.upcomingGames) ? copy.upcomingGames : [];
  // Between seasons the form is swapped for the off-season card; the pitch on
  // the left stays so the section still reads. `apply.status` is set in the
  // admin Copy tab ("open" / "closed").
  const closed =
    String(copy.status || "open")
      .trim()
      .toLowerCase() === "closed";
  const closedCopy = copy.closed || {};

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const scrollToError = (errs) => {
    const order = ["name", "email", "date", "about"];
    const first = order.find((k) => errs[k]);
    if (!first) return;
    const el = fieldRefs[first]?.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const top = window.scrollY + rect.top - Math.max(80, window.innerHeight * 0.15);
    window.scrollTo({ top, behavior: "smooth" });
    if (first !== "date") {
      const focusable = el.querySelector("input, textarea");
      if (focusable) setTimeout(() => focusable.focus({ preventScroll: true }), 400);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!form.email.includes("@")) errs.email = "Need a valid email";
    if (!form.date) errs.date = "Pick a game";
    if (!form.about.trim() || form.about.trim().length < 20) errs.about = "Tell me a bit more";
    if (Object.keys(errs).length) {
      setErrors(errs);
      scrollToError(errs);
      return;
    }
    setErrors({});

    // Submit to API endpoint. Falls back gracefully if missing.
    try {
      await fetch("/api/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
    } catch (err) {
      console.warn("Apply submission failed; showing success state anyway", err);
    }
    setSubmitted(true);
  };

  // When success state renders, pull the user's viewport to the top of the
  // section so they actually see the confirmation instead of staring at the
  // footer (or wherever they were scrolled when they hit submit).
  useEffect(() => {
    if (!submitted) return;
    const el = sectionRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const top = window.scrollY + rect.top - 24;
    window.scrollTo({ top, behavior: "smooth" });
  }, [submitted]);

  if (submitted) {
    return (
      <section
        ref={sectionRef}
        id="apply"
        data-screen-label="05 Sit Next To Me"
        className="section apply"
      >
        <div className="section-inner apply-success">
          <Reveal>
            <Eyebrow index="05">{copy.successEyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="display h-lg" style={{ marginTop: 24 }}>
              {copy.successTitle}
              <br />
              <em>{copy.successTitleEmphasis}</em>.
            </h2>
          </Reveal>
          <Reveal delay={240}>
            <div className="apply-ticket">
              <div className="apply-ticket-stub">
                <div className="mono apply-ticket-tag">DIAMOND CLUB · APPLICATION RECEIVED</div>
                <div className="apply-ticket-name">{form.name}</div>
                <div className="apply-ticket-row">
                  <div>
                    <span className="mono">SEATS</span>
                    <strong>ROW 2 · 1–3</strong>
                  </div>
                  <div>
                    <span className="mono">DATE</span>
                    <strong>{form.date}</strong>
                  </div>
                  <div>
                    <span className="mono">GROUP</span>
                    <strong>{form.group}</strong>
                  </div>
                </div>
              </div>
              <div className="apply-ticket-perf">
                <div className="apply-ticket-num mono">
                  <span className="apply-ticket-num-prefix">№</span>
                  <span className="apply-ticket-num-val">
                    {Math.floor(Math.random() * 9000 + 1000)}
                  </span>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={360}>
            <p className="lede" style={{ marginTop: 32 }}>
              {copy.successText}
            </p>
          </Reveal>
          <Reveal delay={480}>
            <Btn
              ghost
              onClick={() => {
                setSubmitted(false);
                setForm({ name: "", email: "", date: "", group: "2", about: "", how: "" });
              }}
            >
              Submit another
            </Btn>
          </Reveal>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      id="apply"
      data-screen-label="05 Sit Next To Me"
      className="section apply"
    >
      <div className="section-inner">
        <div className="apply-head">
          <Reveal>
            <Eyebrow index="05">{copy.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="display h-xl apply-title">
              {copy.title} <em>{copy.titleEmphasis}</em>.
            </h2>
          </Reveal>
        </div>

        <div className="apply-grid">
          <div className="apply-text">
            <Reveal delay={200}>
              <p className="lede">{copy.lede}</p>
            </Reveal>
            <Reveal delay={300}>
              <ul className="apply-list">
                {copy.bullets.map((item, index) => (
                  <li key={index}>
                    <span className="mono">{String(index + 1).padStart(2, "0")}</span> {item}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={400}>
              <div className="apply-callout">
                <span className="mono">{copy.calloutTag}</span>
                <span>{copy.calloutText}</span>
              </div>
            </Reveal>
          </div>

          <Reveal delay={300} className="apply-card-wrap">
            {closed ? (
              <div className="apply-card apply-card-closed">
                <div className="apply-card-tag mono">{closedCopy.tag}</div>
                <div className="apply-closed-body">
                  <div className="apply-closed-seats" aria-hidden="true">
                    <span>1</span>
                    <span className="is-greg">2</span>
                    <span>3</span>
                  </div>
                  <h3 className="apply-closed-title">
                    {closedCopy.title} <em>{closedCopy.titleEmphasis}</em>.
                  </h3>
                  <p className="apply-closed-text">{closedCopy.text}</p>
                </div>
                <p className="apply-fine mono">{closedCopy.note}</p>
              </div>
            ) : (
              <form className="apply-card" onSubmit={submit}>
                <div className="apply-card-tag mono">{copy.formTag}</div>

                <div className="apply-row">
                  <label
                    ref={fieldRefs.name}
                    className={`apply-field ${errors.name ? "has-err" : ""}`}
                  >
                    <span className="apply-label">Name</span>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => updateField("name", e.target.value)}
                      placeholder="Your full name"
                      autoComplete="name"
                    />
                    {errors.name && <span className="apply-err">{errors.name}</span>}
                  </label>
                  <label
                    ref={fieldRefs.email}
                    className={`apply-field ${errors.email ? "has-err" : ""}`}
                  >
                    <span className="apply-label">Email</span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      placeholder="you@email.com"
                      autoComplete="email"
                      inputMode="email"
                    />
                    {errors.email && <span className="apply-err">{errors.email}</span>}
                  </label>
                </div>

                <div ref={fieldRefs.date} className={`apply-field ${errors.date ? "has-err" : ""}`}>
                  <span className="apply-label">Pick a game</span>
                  {upcoming.length === 0 ? (
                    <input
                      type="text"
                      value={form.date}
                      onChange={(e) => updateField("date", e.target.value)}
                      placeholder="Which home game? Date and opponent"
                    />
                  ) : (
                    <div className="apply-dates" role="radiogroup" aria-label="Upcoming games">
                      {upcoming.map((d) => {
                        const active = form.date === d;
                        return (
                          <button
                            type="button"
                            key={d}
                            role="radio"
                            aria-checked={active}
                            className={`apply-date ${active ? "is-active" : ""}`}
                            onClick={() => updateField("date", d)}
                          >
                            {d}
                            <span className="apply-date-check" aria-hidden>
                              ✓
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {errors.date && <span className="apply-err">{errors.date}</span>}
                </div>

                <label className="apply-field">
                  <span className="apply-label">
                    How many in your party? (max 2 — you'll sit next to me)
                  </span>
                  <div className="apply-radio" role="radiogroup" aria-label="Party size">
                    {["1", "2"].map((g) => (
                      <button
                        type="button"
                        key={g}
                        role="radio"
                        aria-checked={form.group === g}
                        className={`apply-radio-btn ${form.group === g ? "is-active" : ""}`}
                        onClick={() => updateField("group", g)}
                      >
                        {g} {g === "1" ? "person" : "people"}
                      </button>
                    ))}
                  </div>
                </label>

                <label
                  ref={fieldRefs.about}
                  className={`apply-field ${errors.about ? "has-err" : ""}`}
                >
                  <span className="apply-label">Why do you want to come?</span>
                  <textarea
                    rows="4"
                    value={form.about}
                    onChange={(e) => updateField("about", e.target.value)}
                    placeholder="Tell me a story. Bonus points if it involves the '85 series, your dad, or both."
                  />
                  {errors.about && <span className="apply-err">{errors.about}</span>}
                </label>

                <label className="apply-field">
                  <span className="apply-label">
                    How'd you find me? <em className="apply-opt">optional</em>
                  </span>
                  <input
                    type="text"
                    value={form.how}
                    onChange={(e) => updateField("how", e.target.value)}
                    placeholder="Facebook, the book, a friend..."
                  />
                </label>

                <button type="submit" className="apply-submit">
                  Submit Application <span aria-hidden>→</span>
                </button>

                <p className="apply-fine mono">{copy.finePrint}</p>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const applyStyles = document.createElement("style");
applyStyles.textContent = `
.apply { background: var(--navy-900); position: relative; overflow: hidden; }
.apply::before {
  content: ""; position: absolute; inset: 0;
  background: radial-gradient(ellipse at 80% 0%, rgba(0, 70, 135, 0.2) 0%, transparent 50%);
  pointer-events: none;
}
.apply-head { margin-bottom: 64px; }
.apply-title { color: var(--bone); margin-top: 24px; }
.apply-title em { color: var(--royal-blue-glow); font-style: italic; }

.apply-grid {
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 80px;
  align-items: start;
  position: relative;
}
@media (max-width: 980px) { .apply-grid { grid-template-columns: 1fr; gap: 48px; } }

.apply-list { list-style: none; padding: 0; margin: 32px 0 0 0; display: flex; flex-direction: column; gap: 16px; max-width: 460px; }
.apply-list li {
  display: flex; gap: 16px; align-items: flex-start;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--rule);
  font-size: 1rem; color: var(--bone-dim);
}
.apply-list .mono { color: var(--gold); font-size: 0.6875rem; letter-spacing: 0.18em; padding-top: 4px; }

.apply-callout {
  margin-top: 36px;
  padding: 18px 22px;
  border-left: 2px solid var(--royal-blue-glow);
  background: linear-gradient(90deg, rgba(43,138,255,0.08), transparent 70%);
  display: flex; flex-direction: column; gap: 8px;
}
.apply-callout .mono { font-size: 0.625rem; letter-spacing: 0.2em; color: var(--royal-blue-glow); }
.apply-callout span:last-child { font-family: var(--serif); font-style: italic; font-size: 1.0625rem; color: var(--bone); line-height: 1.4; }

.apply-card {
  background: linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01));
  border: 1px solid var(--rule);
  padding: 40px;
  border-radius: 4px;
  display: flex; flex-direction: column; gap: 24px;
  position: relative;
  backdrop-filter: blur(20px);
}
.apply-card-tag {
  font-size: 0.625rem; letter-spacing: 0.22em; color: var(--gold);
  border-bottom: 1px solid var(--rule);
  padding-bottom: 14px;
}
.apply-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
@media (max-width: 540px) { .apply-row { grid-template-columns: 1fr; } }
.apply-field { display: flex; flex-direction: column; gap: 8px; }
.apply-label { font-family: var(--mono); font-size: 0.6875rem; letter-spacing: 0.18em; color: var(--gold); text-transform: uppercase; }
.apply-opt { color: var(--bone-dim); font-style: italic; text-transform: none; opacity: 0.7; font-family: var(--serif); letter-spacing: 0; }
.apply-card input, .apply-card textarea {
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid var(--rule);
  padding: 14px 16px;
  color: var(--bone);
  font-family: var(--sans);
  font-size: 0.875rem;
  border-radius: 2px;
  outline: none;
  transition: border-color 200ms;
}
.apply-card input:focus, .apply-card textarea:focus { border-color: var(--royal-blue-glow); }
.apply-card textarea { resize: vertical; min-height: 100px; }

.apply-dates { display: flex; flex-wrap: wrap; gap: 8px; }
.apply-date {
  position: relative;
  background: transparent;
  border: 1px solid var(--rule);
  padding: 12px 16px;
  min-height: 44px;
  font-family: var(--mono);
  font-size: 0.75rem;
  color: var(--bone);
  cursor: pointer;
  border-radius: 2px;
  transition: background 160ms, border-color 160ms, color 160ms;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;
  display: inline-flex; align-items: center; gap: 8px;
  line-height: 1;
}
.apply-date .apply-date-check {
  display: inline-block;
  width: 0; overflow: hidden;
  opacity: 0;
  font-size: 0.75rem;
  transition: width 160ms, opacity 160ms;
}
.apply-date.is-active .apply-date-check { width: 12px; opacity: 1; }
.apply-date:focus { outline: none; }
.apply-date:focus-visible { outline: 2px solid var(--royal-blue-glow); outline-offset: 2px; }
@media (hover: hover) {
  .apply-date:hover { border-color: var(--royal-blue-glow); color: var(--royal-blue-glow); }
}
.apply-date.is-active {
  background: var(--royal-blue-bright);
  color: var(--bone);
  border-color: var(--royal-blue-glow);
  box-shadow: 0 0 0 1px var(--royal-blue-glow) inset;
}

.apply-radio { display: flex; gap: 8px; }
.apply-radio-btn {
  flex: 1;
  background: transparent;
  border: 1px solid var(--rule);
  padding: 14px 16px;
  min-height: 48px;
  color: var(--bone);
  font-family: var(--sans);
  font-size: 0.875rem;
  cursor: pointer;
  border-radius: 2px;
  transition: background 160ms, border-color 160ms, color 160ms;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;
}
.apply-radio-btn.is-active { background: var(--royal-blue-bright); color: var(--bone); border-color: var(--royal-blue-glow); box-shadow: 0 0 0 1px var(--royal-blue-glow) inset; }

.apply-field.has-err input,
.apply-field.has-err textarea { border-color: var(--crimson); }
.apply-field.has-err .apply-dates {
  outline: 1px solid var(--crimson);
  outline-offset: 6px;
  border-radius: 2px;
}

@media (max-width: 540px) {
  .apply-dates { gap: 6px; }
  .apply-date { padding: 12px 14px; font-size: 0.75rem; flex: 1 1 calc(50% - 6px); justify-content: center; }
  .apply-card { padding: 28px 22px !important; }
}

.apply-submit {
  background: linear-gradient(135deg, var(--royal-blue-bright), var(--royal-blue));
  color: var(--bone);
  border: 1px solid var(--royal-blue-glow);
  padding: 16px 24px;
  font-family: var(--sans);
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  border-radius: 999px;
  margin-top: 8px;
  transition: all 200ms;
  box-shadow: 0 8px 30px -8px rgba(0, 100, 194, 0.5);
}
.apply-submit:hover { background: linear-gradient(135deg, var(--royal-blue-glow), var(--royal-blue-bright)); transform: translateY(-1px); box-shadow: 0 14px 40px -8px rgba(43, 138, 255, 0.6); }

.apply-err { font-family: var(--mono); font-size: 0.6875rem; color: var(--crimson); letter-spacing: 0.1em; }
.apply-fine { font-size: 0.6875rem; letter-spacing: 0.16em; color: var(--bone-dim); text-align: center; opacity: 0.6; text-transform: uppercase; }

/* Off-season card: same frame as the form, three seats with Greg's in the
   middle, and a date to come back. */
.apply-card-closed { gap: 28px; }
.apply-closed-seats { display: flex; gap: 8px; margin-bottom: 22px; }
.apply-closed-seats span {
  width: 40px; height: 36px;
  border: 1px solid var(--rule-strong);
  border-radius: 7px 7px 3px 3px;
  display: grid; place-items: center;
  font-family: var(--mono); font-size: 0.6875rem; letter-spacing: 0.04em;
  color: var(--bone-dim); opacity: 0.6;
}
.apply-closed-seats span.is-greg {
  background: var(--royal-blue);
  border-color: var(--royal-blue-glow);
  color: var(--bone); opacity: 1;
  box-shadow: 0 0 0 1px var(--royal-blue-glow) inset, 0 10px 24px -10px rgba(43, 138, 255, 0.7);
}
.apply-card .apply-closed-title {
  margin: 0;
  font-family: var(--serif); font-weight: 600; letter-spacing: -0.02em;
  font-size: clamp(2rem, 3.4vw, 2.75rem); line-height: 1.05;
  color: var(--bone);
}
.apply-card .apply-closed-title em { color: var(--royal-blue-glow); font-style: italic; }
.apply-closed-text {
  margin: 18px 0 0;
  font-family: var(--serif); font-size: 1.0625rem; line-height: 1.55;
  color: var(--bone-dim); max-width: 46ch;
}
@media (max-width: 540px) {
  .apply-card-closed { gap: 22px; }
  .apply-closed-text { font-size: 1rem; }
}

.apply-success { text-align: left; max-width: 720px; }
.apply-success em { color: var(--gold); font-style: italic; }
.apply-ticket {
  display: flex; margin-top: 48px;
  background: var(--bone); color: var(--navy-900);
  border-radius: 4px; overflow: hidden;
  box-shadow: 0 30px 60px -20px rgba(0,0,0,0.6);
  position: relative;
}
.apply-ticket::before {
  content: ""; position: absolute;
  left: calc(100% - 140px); top: 0; bottom: 0;
  width: 1px;
  border-left: 2px dashed var(--navy-900);
  opacity: 0.3;
}
.apply-ticket-stub { flex: 1; padding: 32px; }
.apply-ticket-tag { font-size: 0.625rem; letter-spacing: 0.22em; color: var(--royal-blue); }
.apply-ticket-name { font-family: var(--serif); font-size: 2.25rem; font-weight: 600; margin-top: 12px; letter-spacing: -0.01em; }
.apply-ticket-row { display: flex; gap: 32px; margin-top: 24px; }
.apply-ticket-row > div { display: flex; flex-direction: column; gap: 4px; }
.apply-ticket-row .mono { font-size: 0.625rem; letter-spacing: 0.16em; color: var(--royal-blue); }
.apply-ticket-row strong { font-family: var(--serif); font-size: 1.125rem; font-weight: 600; }
.apply-ticket-perf { width: 140px; background: var(--royal-blue); color: var(--bone); display: grid; place-items: center; }
.apply-ticket-num { font-size: 0.875rem; letter-spacing: 0.18em; text-align: center; display: flex; flex-direction: column; gap: 6px; align-items: center; line-height: 1; }

@media (max-width: 640px) {
  .apply-success h2 { font-size: clamp(1.875rem, 7.5vw, 2.75rem) !important; }
  .apply-ticket { margin-top: 32px !important; }
  .apply-ticket-stub { padding: 22px !important; }
  .apply-ticket-tag { font-size: 0.5625rem !important; letter-spacing: 0.18em !important; }
  .apply-ticket-name { font-size: 1.625rem !important; margin-top: 8px !important; line-height: 1.1 !important; word-break: break-word !important; }
  .apply-ticket-row { gap: 14px 22px !important; margin-top: 18px !important; flex-wrap: wrap !important; }
  .apply-ticket-row > div { flex: 0 0 auto; }
  .apply-ticket-row strong { font-size: 0.9375rem !important; }
  .apply-ticket-perf { width: 100% !important; padding: 14px 22px !important; place-items: stretch; }
  .apply-ticket-num { flex-direction: row !important; gap: 10px !important; justify-content: center; font-size: 0.8125rem !important; }
  .apply-success .lede { font-size: 0.9375rem !important; margin-top: 24px !important; }
}
`;
document.head.appendChild(applyStyles);
